import { createContext, type FC, type ReactNode, useContext, useEffect, useState } from "react"
import { Trans } from "react-i18next"
import { useSearchParams } from "react-router"

import { exhaustiveGuard } from "@forento/shared/utilities/switch"

import { useThemeComponents } from "~/contexts/ThemeComponentContext"
import PartialLoadingPage from "~/themes/school/components/PartialLoadingPage"
import { useTranslation } from "~/translations"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

interface Value {
	show(): void
}

const Context = createContext<Value>({
	show() {
		throw new Error("PurchaseSuccessModalContext is not in tree!")
	},
})

export const usePurchaseSuccessModal = () => useContext(Context)

const parameterKey = "stripe-session-id"

export type PaypalOrder = { userStatus: "existing" | "assigned" | "created"; email: string }

type ProviderProps = {
	content: { type: "course"; id: number } | { type: "video" | "downloadable" | "bundle" | "session" | "event" }
	paypalOrder: PaypalOrder | null
	children: ReactNode
}
export const PurchaseSuccessModalContextProvider: FC<ProviderProps> = ({ content, paypalOrder, children }) => {
	const { Button, Modal } = useThemeComponents()
	const [searchParams, setSearchParams] = useSearchParams()
	const t = useTranslation()

	const stripeSessionId = searchParams.get(parameterKey)

	const stripeOrder = query.payment.stripe.getCompletedSessionOrder.useQuery(stripeSessionId ?? "", {
		enabled: stripeSessionId !== null,
		staleTime: Infinity,
	})

	const [isOpen, setOpen] = useState(false)

	useEffect(() => {
		if (stripeSessionId !== null) {
			setOpen(true)
		}
	}, [stripeSessionId])

	function handleClose() {
		setSearchParams(current => {
			const newParams = new URLSearchParams(current)
			newParams.delete(parameterKey)
			return newParams
		})
		setOpen(false)
	}

	const order = paypalOrder ?? stripeOrder.data

	return (
		<Context.Provider value={{ show: () => setOpen(true) }}>
			<Modal.Frame isOpen={isOpen}>
				{order === undefined ? (
					<PartialLoadingPage />
				) : order === null ? (
					<>
						<Modal.Title>{t("general.error")}</Modal.Title>
						<Modal.Text>{t("product.failedToLoadOrder")}</Modal.Text>
					</>
				) : (
					<>
						<Modal.Title>{t("product.purchaseSuccessful")}</Modal.Title>
						<Modal.Text>
							<Trans
								i18nKey="product.purchaseSuccessfulMessage"
								values={{ type: t(`product.type.${content.type}`) }}
								components={{ strong: <strong /> }}
							/>
						</Modal.Text>

						{order.userStatus === "existing" ? (
							<Modal.Text>
								<Trans
									i18nKey="product.purchaseReceiptSent"
									values={{ email: order.email }}
									components={{ strong: <strong /> }}
								/>
							</Modal.Text>
						) : order.userStatus === "assigned" ? (
							<Modal.Text>
								<Trans
									i18nKey="product.purchaseAssignedToAccount"
									values={{ email: order.email }}
									components={{ strong: <strong /> }}
								/>
							</Modal.Text>
						) : order.userStatus === "created" ? (
							<Modal.Text>
								<Trans
									i18nKey="product.purchaseCreatedNewAccount"
									values={{ email: order.email }}
									components={{ strong: <strong /> }}
								/>
							</Modal.Text>
						) : (
							exhaustiveGuard(order.userStatus)
						)}
					</>
				)}
				<Modal.Buttons>
					<Button variant="secondary" onClick={handleClose}>
						{t("close")}
					</Button>
					{content.type === "course" && order?.userStatus === "existing" && (
						<Button variant="primary" onClick={routes.course.player(content.id)}>
							{t("course.start")}
						</Button>
					)}
				</Modal.Buttons>
			</Modal.Frame>
			{children}
		</Context.Provider>
	)
}
